'use client'

import { useEffect, useRef } from 'react'
import { type CategoryDTO, Currency, type Option, type ProductLightDTO } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { overrideText } from 'ui/lib/overrides'
import { type CurrencyOverrides } from '../../types'
import useGoogleAnalytics from './use-google-analytics'

interface TrackViewItemListParams {
  categories: Option<CategoryDTO[]>
  products: ProductLightDTO[] | undefined
  id: string
  listName?: string
  currency?: Currency
  isLoading?: boolean
}

export const useTrackViewItemList = ({
  categories,
  products,
  id,
  listName,
  currency = Currency.SEK,
  isLoading = false
}: TrackViewItemListParams) => {
  const GA = useGoogleAnalytics({ categories })
  const overrides = useOverridesContext<keyof CurrencyOverrides>()

  const previousTrackingKeyRef = useRef<string | null>(null)
  const previousIsLoadingRef = useRef<boolean>(false)

  useEffect(() => {
    previousTrackingKeyRef.current = null
    previousIsLoadingRef.current = false
  }, [id])

  useEffect(() => {
    const wasLoading = previousIsLoadingRef.current
    const justFinishedLoading = wasLoading && !isLoading
    previousIsLoadingRef.current = isLoading

    if (isLoading) return
    if (products === undefined) return
    if (products.length === 0 && !justFinishedLoading) return

    const trackingKey = JSON.stringify({
      length: products.length,
      ids: products.map((p) => p.id),
      searchId: id
    })

    if (trackingKey !== previousTrackingKeyRef.current) {
      const effectiveCurrency = overrideText(currency, overrides?.currency) as Currency
      GA.viewItemList(products, id, listName, effectiveCurrency)
      previousTrackingKeyRef.current = trackingKey
    }
  }, [GA, products, id, listName, currency, overrides?.currency, isLoading])
}
